import React, { useMemo } from 'react'
import UnifiedQuickSearch from './UnifiedQuickSearch'
import { fundersSearchConfig } from '../config/searchConfigs'
import { useList } from '@refinedev/core'

interface FundersQuickSearchProps {
  onSearchChange?: (searchText: string, searchType?: string) => void
}

export default function FundersQuickSearch({ onSearchChange }: FundersQuickSearchProps) {
  // Fetch regions data for the region autocomplete
  const { data: regionsData } = useList({
    resource: 'regions',
    pagination: { pageSize: 5000, mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
  })

  // Fetch funder types for the type autocomplete
  const { data: typesData } = useList({
    resource: 'funders/types',
    pagination: { mode: 'off' },
    queryOptions: {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  })

  // Create dynamic config with fetched data
  const config = useMemo(() => {
    const dynamicConfig = { ...fundersSearchConfig }

    // Update region options for funder region field
    if (regionsData?.data) {
      const regionOptions = [
        { value: { id: 'no_region' }, label: 'None' },
        ...regionsData.data.map(region => ({
          value: region,
          label: region.name
        }))
      ]

      // Find and update funder region_id field
      const funderRegionField = dynamicConfig.advancedFilterRows[1]?.fields.find(f => f.key === 'region_id')
      if (funderRegionField) {
        funderRegionField.options = regionOptions
      }
    }

    // Update type options for funder type field
    if (typesData?.data) {
      const typeOptions = (typesData.data as unknown as string[]).map((type) => ({
        value: type,
        label: type,
      }))

      // Find and update type field
      const typeField = dynamicConfig.advancedFilterRows[0]?.fields.find(f => f.key === 'type')
      if (typeField) {
        typeField.options = typeOptions
      }
    }

    return dynamicConfig
  }, [regionsData, typesData])

  return (
    <UnifiedQuickSearch
      config={config}
      onSearchChange={onSearchChange}
      resourceName="funders"
    />
  )
}
